/** @jsx jsx */
/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, max-len, no-irregular-whitespace */
import React from 'react';
import { css, jsx } from '@emotion/core';
import { BoardData } from 'domain/attend/shiftBoard/types';
import { DOW_LABELS, parseDateStr } from 'domain/attend/shiftBoard/time';
import { mock } from './styles';

const styles = {
  bar: css({
    margin: '8px 16px 0', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 16px',
  }),
  icon: css({
    width: '18px', height: '18px', borderRadius: '9999px', color: '#fff', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
  }),
  title: css({ fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }),
  detail: css({
    fontSize: '11px', color: mock.textSub, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }),
  chip: css({
    background: '#fff', border: `1px solid ${mock.error}`, color: mock.secondary, borderRadius: '9999px', fontSize: '11px', fontWeight: 700, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
  }),
};

const md = (s: string): string => {
  const d = parseDateStr(s);
  return `${d.getMonth() + 1}/${d.getDate()}（${DOW_LABELS[d.getDay()]}）`;
};
const yen = (n: number): string => `¥${Math.round(n).toLocaleString('ja-JP')}`;

export type AlertBannersProps = {
  board: BoardData | null;
  onFocusCell: (staffCode: string, date: string) => void;
};

/**
 * モック準拠の注意バナー（予定人数超過／人件費予算超過／労務ルール）
 *
 * 未通知の変更の帯は 9/24 に外した。確定済みの日は反映した時点で確定し直すので利用者の操作は要らず、
 * 翌朝の確定通知に載ることは反映時の通知で伝えている。
 */
const AlertBanners: React.FC<AlertBannersProps> = ({ board, onFocusCell }) => {
  if (!board) {
    return null;
  }
  const { total, byDate } = board.summary;
  const names: { [code: string]: string } = {};
  board.staffs.forEach((s) => {
    names[s.staffCode] = s.staffName;
  });
  const overDays = total.headcountOverDays;
  const underDays = total.headcountUnderDays;
  const costOverDays = total.laborCostOverDays;
  const byDateMap: { [d: string]: { headcount: number; plannedHeadcount: number | null; laborCost: number | null; laborCostBudget: number | null } } = {};
  byDate.forEach((d) => {
    byDateMap[d.date] = d;
  });
  const costOverTotal = costOverDays.reduce((t, d) => {
    const x = byDateMap[d];
    return t + (x && x.laborCost !== null && x.laborCostBudget !== null ? x.laborCost - x.laborCostBudget : 0);
  }, 0);

  return (
    <div>
      {overDays.length > 0 && (
        <div css={styles.bar} style={{ background: mock.amberBg, border: `1px solid ${mock.amberBorder}` }}>
          <span css={styles.icon} style={{ background: mock.amberMark }}>!</span>
          <span css={styles.title} style={{ color: mock.amberText }}>{`予定人数を超過している日が ${overDays.length}日あります`}</span>
          <span css={styles.detail}>
            {overDays.slice(0, 8).map((d) => { const x = byDateMap[d]; return `${md(d)}+${x && x.plannedHeadcount !== null ? x.headcount - x.plannedHeadcount : '?'}名`; }).join(' / ')}
            {overDays.length > 8 ? ` ほか${overDays.length - 8}日` : ''}
            　※予定人数を超えた割当です
          </span>
        </div>
      )}
      {underDays.length > 0 && (
        <div css={styles.bar} style={{ background: mock.errorBg, border: `1px solid ${mock.secondary}` }}>
          <span css={styles.icon} style={{ background: mock.secondary }}>!</span>
          <span css={styles.title} style={{ color: mock.secondary }}>{`予定人数に足りない日が ${underDays.length}日あります`}</span>
          <span css={styles.detail}>
            {underDays.slice(0, 8).map((d) => { const x = byDateMap[d]; return `${md(d)}−${x && x.plannedHeadcount !== null ? x.plannedHeadcount - x.headcount : '?'}名`; }).join(' / ')}
            {underDays.length > 8 ? ` ほか${underDays.length - 8}日` : ''}
          </span>
        </div>
      )}
      {costOverDays.length > 0 && (
        <div css={styles.bar} style={{ background: mock.errorBg, border: `1px solid ${mock.secondary}` }}>
          <span css={styles.icon} style={{ background: mock.secondary }}>!</span>
          <span css={styles.title} style={{ color: mock.secondary }}>{`人件費予算を超過している日が ${costOverDays.length}日あります`}</span>
          <span css={styles.detail}>
            {costOverDays.slice(0, 6).map((d) => { const x = byDateMap[d]; return `${md(d)}${x && x.laborCost !== null && x.laborCostBudget !== null ? `+${yen(x.laborCost - x.laborCostBudget)}` : ''}`; }).join(' / ')}
            {costOverDays.length > 6 ? ` ほか${costOverDays.length - 6}日` : ''}
            {costOverTotal > 0 ? `　超過合計 ${yen(costOverTotal)}` : ''}
          </span>
        </div>
      )}
      {board.violations.length > 0 && (
        <div css={styles.bar} style={{ background: mock.errorBg, border: `1px solid ${mock.error}`, flexWrap: 'wrap' }}>
          <span css={styles.icon} style={{ background: mock.secondary, color: '#fff' }}>!</span>
          <span css={styles.title} style={{ color: mock.secondary }}>{`労務ルール違反 ${board.violations.length}件`}</span>
          {board.violations.map((v) => (
            <span
              key={`${v.staffCode}-${v.type}`}
              css={styles.chip}
              title={`${v.dates.map(md).join(', ')} が対象。クリックで該当セルへ`}
              onClick={() => onFocusCell(v.staffCode, v.dates[0])}
            >
              {`${names[v.staffCode] || v.staffCode}：${v.message}`}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertBanners;
